/**
 * Menu.tsx — Pantalla principal (Menú de opciones)
 *
 * Es la pantalla que ve el usuario después de iniciar sesión.
 * Muestra la tarjeta de bienvenida con los datos del cliente.
 * La navegación (Pagar Ahora, Instalaciones, Promesa, Mis Clientes, etc.)
 * vive en los componentes compartidos AppNavbar (barra superior) y
 * BottomNav (barra inferior + panel "Más"), visibles en todas las pantallas.
 *
 * Seguridad: el cierre de sesión tras 5 minutos sin actividad lo hace
 * CierreInactividad (App.tsx) en toda la app, no solo aquí.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AppNavbar from '../components/AppNavbar';   // Barra de navegación superior
import BottomNav from '../components/BottomNav';   // Barra de navegación inferior + panel "Más"
import AyudaModal from '../components/AyudaModal'; // Modal de ayuda y soporte
import { Container, Badge, Button } from 'react-bootstrap';
import { FaMobileAlt, FaPlug } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { olvidarDetalleAbierto } from '../utils/detalleClienteAbierto';
import './Menu.css';

// Leemos la versión actual de package.json para mostrarla en pantalla
// eslint-disable-next-line @typescript-eslint/no-var-requires
const APP_VERSION: string = (require('../../package.json') as { version: string }).version;

// Los campos codificados como URL a veces vienen doblemente codificados (%2520 en vez de %20).
// Esta función los decodifica hasta 2 veces, tolerando errores si el valor no está codificado.
const decodeDoble = (raw?: string): string => {
  if (!raw) return '';
  let value = raw;
  for (let i = 0; i < 2; i++) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch {
      break;
    }
  }
  return value;
};

// Clave del campo personalizado de Mikrowisp donde se guarda el OID de GenieACS
const CLAVE_OID_GENIEACS = '1770612677';

// El campo "personalizados" de cada servicio viene como JSON string (a veces ya es objeto)
const parsePersonalizados = (raw: unknown): Record<string, string> => {
  try {
    if (typeof raw === 'string') return JSON.parse(raw);
    return (raw as Record<string, string>) || {};
  } catch {
    return {};
  }
};

// Parser para el campo "smartolt", que viene como cadena PHP-serialized (a:17:{s:6:"olt_id";s:1:"9";...})
const parsePhpSerialized = (raw: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!raw || typeof raw !== 'string') return result;
  const re = /s:\d+:"([^"]*)"/g;
  let m: RegExpExecArray | null;
  let lastKey = '';
  let idx = 0;
  while ((m = re.exec(raw)) !== null) {
    if (idx % 2 === 0) lastKey = m[1];
    else if (lastKey) { result[lastKey] = m[1]; lastKey = ''; }
    idx++;
  }
  return result;
};

// Formatea "2026-07-01 17:38:43" → "01/07 17:38" (compacto para la tarjeta de la ONT)
const formatFecha = (raw?: string): string | undefined => {
  if (!raw || raw.startsWith('0000-00-00')) return undefined;
  const [fecha, hora] = raw.split(' ');
  const [anio, mes, dia] = fecha.split('-');
  return `${dia}/${mes} ${hora?.slice(0, 5) || ''}`.trim();
};

// Limpia el nombre del plan de velocidad, ej. "plan_700_Mbps" → "700 Mbps"
const formatPlan = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  return raw.replace(/^plan_/, '').replace(/_/g, ' ');
};

const Menu: React.FC = () => {
  const navigate = useNavigate();

  // ── Datos del cliente ────────────────────────────────────────────────────────
  // Recuperamos los datos que se guardaron en el Login. Los guardamos en estado para
  // poder refrescarlos cada vez que se entra al Menú (ver useEffect de re-consulta).
  const clientDataJSON = localStorage.getItem('clientData');
  const [clientData, setClientData] = useState<any>(() => clientDataJSON ? JSON.parse(clientDataJSON) : null);

  const [showAyudaModal, setShowAyudaModal] = useState(false); // Se abre al tocar el texto de versión

  // Volver al inicio termina la "visita" al cliente que el administrador tenía abierto
  // en Clientes: la próxima vez que entre a Clientes verá la lista, no ese cliente.
  useEffect(() => {
    olvidarDetalleAbierto();
  }, []);

  // Al entrar al Menú (también al volver desde otra pantalla), re-consultamos los datos del
  // cliente por su cédula para reflejar su estado actual (ej: si ya se activó tras una promesa/pago).
  useEffect(() => {
    const url = process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA;
    const ced = clientData?.cedula;
    if (!url || !ced) return;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cedula: ced }),
    })
      .then(r => r.json())
      .then(data => {
        const fresh = Array.isArray(data?.datos) ? data.datos[0] : (data?.datos || data);
        if (fresh && (fresh.id || fresh.nombre)) {
          localStorage.setItem('clientData', JSON.stringify(fresh));
          setClientData(fresh);
        }
      })
      .catch(() => {}); // Si falla, dejamos los datos que ya teníamos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Señal en vivo de la ONT (SmartOLT, vía N8N) ──────────────────────────────
  // Guardamos un resultado por cada "sn" (serial de la ONU) del cliente.
  type OnuSignal = {
    loading: boolean;
    error: boolean;
    status?: string;       // "Online" | "Offline"
    signal?: string;       // etiqueta cualitativa de SmartOLT, ej. "Very good"
    signal1490?: number;   // dBm downstream (el que usamos para el semáforo OK/Alerta)
    signal1310?: number;   // dBm upstream
    oltName?: string;
    zoneName?: string;
    clientName?: string;   // nombre del cliente registrado en la ONU (SmartOLT)
    distanceKm?: string;   // distancia de fibra, ya convertida a km
    lastChange?: string;   // fecha/hora de la última conexión/desconexión, ya formateada
    plan?: string;         // plan de velocidad contratado, ya limpio (ej. "700 Mbps")
    onuType?: string;      // modelo de la ONU, ej. "HG8310M"
    port?: string;         // puerto físico en la OLT
  };
  const [onuSignals, setOnuSignals] = useState<Record<string, OnuSignal>>({});
  const [ontActiveIndex, setOntActiveIndex] = useState(0); // Punto activo del carrusel de ONTs
  const ontCarouselRef = useRef<HTMLDivElement | null>(null);

  const handleOntScroll = () => {
    const el = ontCarouselRef.current;
    if (!el) return;
    const cardWidth = el.children[0]?.clientWidth || 1;
    const gap = 10;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setOntActiveIndex(idx);
  };

  const irASlide = (idx: number) => {
    const el = ontCarouselRef.current;
    const card = el?.children[idx] as HTMLElement | undefined;
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    setOntActiveIndex(idx);
  };

  useEffect(() => {
    const url = process.env.REACT_APP_N8N_URL_ONU_SIGNAL;
    if (!url || !clientData?.servicios) return;

    clientData.servicios.forEach((srv: any) => {
      const smartolt = parsePhpSerialized(srv.smartolt || '');
      const sn = smartolt.sn;
      if (!sn) return;

      setOnuSignals(prev => ({ ...prev, [sn]: { loading: true, error: false } }));

      axios.post(url, { sn })
        .then(res => {
          const onu = res.data?.onus?.[0];
          if (!onu) throw new Error('sin datos');
          setOnuSignals(prev => ({
            ...prev,
            [sn]: {
              loading: false,
              error: false,
              status: onu.status,
              signal: onu.signal,
              signal1490: onu.signal_1490 !== undefined ? parseFloat(onu.signal_1490) : undefined,
              signal1310: onu.signal_1310 !== undefined ? parseFloat(onu.signal_1310) : undefined,
              oltName: onu.olt_name,
              zoneName: onu.zone_name,
              clientName: onu.name,
              distanceKm: onu.distance ? `${(parseFloat(onu.distance) / 1000).toFixed(1)} km` : undefined,
              lastChange: formatFecha(onu.last_status_change),
              plan: formatPlan(onu.service_ports?.[0]?.download_speed),
              onuType: onu.onu_type_name,
              port: onu.port,
            },
          }));
        })
        .catch(() => {
          setOnuSignals(prev => ({ ...prev, [sn]: { loading: false, error: true } }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Datos en vivo del router (GenieACS, vía N8N) ─────────────────────────────
  // Guardamos un resultado por cada OID de GenieACS.
  type AcsInfo = {
    loading: boolean;
    error: boolean;
    manufacturer?: string;
    model?: string;
    serial?: string;
    software?: string;
    mac?: string;
    lastInform?: string;   // ya formateado
    lastBoot?: string;     // ya formateado
    networks?: { ssid: string; count: number }[];
    totalHosts?: number;
  };
  const [acsInfos, setAcsInfos] = useState<Record<string, AcsInfo>>({});
  const [acsExpandido, setAcsExpandido] = useState<Record<string, boolean>>({});
  const [ontExpandido, setOntExpandido] = useState<Record<string, boolean>>({});

  // Extrae el OID de GenieACS de un servicio, únicamente si ESE servicio lo tiene en
  // "personalizados" (clave 1770612677). Sin respaldo a Id_Externo_* para no mostrar
  // una tarjeta GenieACS cuando el servicio en realidad no tiene su propio OID.
  // Devolvemos el valor tal cual viene (doble-codificado) porque así lo espera N8N.
  const getOidGenieAcsRaw = (srv: any): string => {
    const personalizados = parsePersonalizados(srv.personalizados);
    return personalizados[CLAVE_OID_GENIEACS] || '';
  };

  useEffect(() => {
    const url = process.env.REACT_APP_N8N_URL_ACS_OID;
    if (!url || !clientData?.servicios) return;

    clientData.servicios.forEach((srv: any) => {
      const oid = getOidGenieAcsRaw(srv);
      if (!oid) return;

      setAcsInfos(prev => ({ ...prev, [oid]: { loading: true, error: false } }));

      axios.post(url, { id_acs: oid })
        .then(res => {
          const data = res.data;
          if (data?.error || !data?.manufacturer) throw new Error(data?.mensaje || 'sin datos');

          setAcsInfos(prev => ({
            ...prev,
            [oid]: {
              loading: false,
              error: false,
              manufacturer: data.manufacturer,
              model: data.model,
              serial: data.serial,
              software: data.softwareVersion,
              mac: data.mac,
              lastInform: formatFecha(data.lastInform),
              lastBoot: formatFecha(data.lastBoot),
              networks: (data.networks || []).map((n: any) => ({ ssid: n.ssid, count: n.count })),
              totalHosts: data.totalHosts,
            },
          }));
        })
        .catch(() => {
          setAcsInfos(prev => ({ ...prev, [oid]: { loading: false, error: true } }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Guardia: si no hay datos del cliente, mostrar mensaje de error ────────────
  if (!clientData) {
    return (
      <Container className="mt-5 text-center">
        <h2>No se encontraron datos del cliente.</h2>
        <Button variant="primary" className="mt-3" onClick={() => navigate('/')}>
          Iniciar Sesión
        </Button>
      </Container>
    );
  }

  // Color del badge de estado: verde=activo, rojo=cualquier otro
  const estado = clientData.estado?.toUpperCase();
  const estadoVariant = estado === 'ACTIVO' ? 'success' : 'danger';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Barra de navegación superior con logo y botón de cerrar sesión */}
      <AppNavbar />

      {/* Contenido principal con scroll */}
      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        <Container className="mt-4">

          {/* ── Tarjeta de bienvenida compacta ── */}
          <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px', marginBottom: 4 }}>
            {/* Fila 1: nombre + badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--sn-marca-fuerte)' }}>👤 {clientData.nombre}</span>
              <Badge bg={estadoVariant} style={{ fontSize: 10 }}>{clientData.estado}</Badge>
            </div>
            {/* Fila 2: datos en línea */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
              {[
                { label: '🪪', value: clientData.cedula },
                { label: '📱', value: clientData.movil || '—' },
                { label: '✉️', value: clientData.correo },
                ...(clientData.facturacion?.facturas_nopagadas
                  ? [{
                      label: '🧾',
                      value: `${clientData.facturacion.facturas_nopagadas} factura(s) · $${parseFloat(clientData.facturacion.total_facturas || '0').toFixed(2)}`,
                    }]
                  : []),
              ].map((item, i) => (
                <span key={i} style={{ fontSize: 11, color: 'var(--sn-texto-3)', whiteSpace: 'nowrap' }}>
                  {item.label} <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{item.value}</span>
                </span>
              ))}
            </div>

          </div>

          {/* ── Carrusel: GenieACS (todos) seguido de Detalles de la ONT (todos) ── */}
          {Array.isArray(clientData.servicios) && clientData.servicios.length > 0 && (() => {
            const serviciosConSn = clientData.servicios
              .map((srv: any) => ({ sn: parsePhpSerialized(srv.smartolt || '').sn }))
              .filter((s: any) => s.sn);

            const serviciosConOid = clientData.servicios
              .map((srv: any) => ({ oid: getOidGenieAcsRaw(srv) }))
              .filter((s: any) => s.oid);

            const totalSlides = serviciosConOid.length + serviciosConSn.length;
            const esCarrusel = totalSlides > 1;

            const cardStyle = {
              background: 'var(--sn-superficie)', borderRadius: 14, border: '1px solid var(--sn-borde)', padding: '10px 14px',
              ...(esCarrusel ? { flex: '0 0 88%', scrollSnapAlign: 'start' as const } : { marginTop: 8 }),
            };

            const renderAcs = (oid: string, i: number) => {
              const info = acsInfos[oid];
              const expandido = !!acsExpandido[oid];
              return (
                <div key={`acs-${oid}`} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>📡 Router (GenieACS)</span>
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-3)', textAlign: 'right' as const, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      Servicio {i + 1} · {[info?.manufacturer, info?.model].filter(Boolean).join(' - ') || decodeDoble(oid)}
                    </span>
                  </div>
                  {info && !info.loading && !info.error && (
                    <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
                        {info.networks?.filter(n => n.ssid).map((n, k) => (
                          <span key={k}>📶 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{n.ssid}</span></span>
                        ))}
                      </div>
                      <span style={{ whiteSpace: 'nowrap' as const }}>{info.totalHosts ?? 0} disp. conectados</span>
                    </div>
                  )}

                  {!info || info.loading ? (
                    <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando router…</div>
                  ) : info.error ? (
                    <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Sin datos del router disponibles.</div>
                  ) : (
                    <>
                      {expandido && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 6px', marginBottom: info.networks?.length ? 8 : 0 }}>
                            {[
                              { label: 'Serie', value: info.serial },
                              { label: 'Software', value: info.software },
                              { label: 'Gestión', value: info.mac },
                              { label: 'Últ. conexión', value: info.lastInform },
                              { label: 'Últ. reinicio', value: info.lastBoot },
                            ].filter(s => s.value).map((s, k) => (
                              <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.label}<br /><span style={{ color: 'var(--sn-texto)', fontWeight: 500, fontSize: 11 }}>{s.value}</span>
                              </div>
                            ))}
                          </div>

                          {info.networks && info.networks.length > 0 && (
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-texto-3)', textTransform: 'uppercase', marginBottom: 4 }}>
                                Redes WiFi
                              </div>
                              {info.networks.map((n, k) => (
                                <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                                  <span>📶 <span style={{ color: 'var(--sn-texto)', fontWeight: 500 }}>{n.ssid}</span></span>
                                  <span>{n.count} disp.</span>
                                </div>
                              ))}
                              <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--sn-borde)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                                <span>Total</span>
                                <span>{info.totalHosts ?? 0} disp.</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <button
                        onClick={() => setAcsExpandido(prev => ({ ...prev, [oid]: !prev[oid] }))}
                        style={{
                          width: '100%', background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 6,
                          padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--sn-marca)', cursor: 'pointer',
                        }}
                      >
                        {expandido ? '▲ Ver menos' : '▼ Ver más'}
                      </button>
                    </>
                  )}
                </div>
              );
            };

            const renderOnt = (sn: string, i: number) => {
              const sig = onuSignals[sn];
              const enRango = sig?.signal1490 !== undefined && sig.signal1490 <= -8 && sig.signal1490 >= -27;
              const esOk = sig?.status?.toLowerCase() === 'online' && enRango;

              // Todos los detalles ocultos detrás de "Ver más"
              const statsSecundarios = sig && !sig.loading && !sig.error ? [
                { label: 'Estado', value: sig.status },
                { label: 'Señal', value: sig.signal },
                { label: 'Plan', value: sig.plan },
                { label: 'RX (1490)', value: sig.signal1490 !== undefined ? `${sig.signal1490} dBm` : undefined },
                { label: 'TX (1310)', value: sig.signal1310 !== undefined ? `${sig.signal1310} dBm` : undefined },
                { label: 'OLT', value: sig.oltName },
                { label: 'Modelo ONU', value: sig.onuType },
                { label: 'Puerto', value: sig.port },
                { label: 'Distancia', value: sig.distanceKm },
                { label: 'Últ. conexión', value: sig.lastChange },
              ].filter(s => s.value) : [];

              const expandidoOnt = !!ontExpandido[sn];

              return (
                <div key={`ont-${sn}`} style={cardStyle}>
                  {/* Encabezado: título + identificador pequeño en la misma línea, igual que la tarjeta de Router */}
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-marca-fuerte)' }}>📶 ONT</span>
                    <span style={{ fontSize: 10, color: 'var(--sn-texto-3)', textAlign: 'right' as const, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      SN {sn}
                    </span>
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{sig?.clientName || clientData.nombre}</span>
                    {sig && !sig.loading && !sig.error && (
                      <span style={{
                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700,
                        color: esOk ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)', background: esOk ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)',
                        border: `1px solid ${esOk ? 'var(--sn-exito)' : 'var(--sn-peligro)'}`, borderRadius: 999, padding: '2px 8px',
                        whiteSpace: 'nowrap' as const,
                      }}>
                        {esOk ? '✅ OK' : '⚠️ Alerta'}
                      </span>
                    )}
                  </div>

                  {!sig || sig.loading ? (
                    <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Consultando señal…</div>
                  ) : sig.error ? (
                    <div style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>Sin datos de señal disponibles.</div>
                  ) : (
                    <>
                      {expandidoOnt && statsSecundarios.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 6px', marginBottom: 8 }}>
                          {statsSecundarios.map((s, k) => (
                            <div key={k} style={{ fontSize: 10, color: 'var(--sn-texto-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.label}<br /><span style={{ color: 'var(--sn-texto)', fontWeight: 500, fontSize: 11 }}>{s.value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {statsSecundarios.length > 0 && (
                        <button
                          onClick={() => setOntExpandido(prev => ({ ...prev, [sn]: !prev[sn] }))}
                          style={{
                            width: '100%', background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 6,
                            padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--sn-marca)', cursor: 'pointer',
                          }}
                        >
                          {expandidoOnt ? '▲ Ver menos' : '▼ Ver más'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            };

            // Orden pedido: todas las GenieACS primero, luego todas las ONT
            const slides = [
              ...serviciosConOid.map((s: any, i: number) => renderAcs(s.oid, i)),
              ...serviciosConSn.map((s: any, i: number) => renderOnt(s.sn, i)),
            ];

            if (slides.length === 0) return null;

            // Fila de accesos rápidos (iconos tipo barra inferior).
            // "Activar Servicio" solo si el cliente está suspendido y con deuda pendiente.
            const mostrarActivar = estado === 'SUSPENDIDO' && (clientData.facturacion?.facturas_nopagadas || 0) > 0;
            const botonDispositivos = (serviciosConOid.length > 0 || mostrarActivar) && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginTop: 10, flexWrap: 'wrap' as const }}>
                {serviciosConOid.length > 0 && (
                  <button
                    onClick={() => navigate('/menu/dispositivos')}
                    style={{
                      background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 10,
                      padding: '8px 10px', cursor: 'pointer', width: 72,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}
                  >
                    <FaMobileAlt size={18} color="var(--sn-marca)" />
                    <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-marca)', textAlign: 'center' as const, lineHeight: 1.2 }}>
                      Dispositivos<br />Conectados
                    </span>
                  </button>
                )}
                {mostrarActivar && (
                  <button
                    onClick={() => navigate('/menu/promesa', { state: { cedula: clientData.cedula } })}
                    style={{
                      background: 'var(--sn-exito-suave)', border: '1px solid var(--sn-exito)', borderRadius: 10,
                      padding: '8px 10px', cursor: 'pointer', width: 72,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}
                  >
                    <FaPlug size={18} color="var(--sn-exito-texto)" />
                    <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sn-exito-texto)', textAlign: 'center' as const, lineHeight: 1.2 }}>
                      Activar<br />Servicio
                    </span>
                  </button>
                )}
              </div>
            );

            return esCarrusel ? (
              <div style={{ marginTop: 8 }}>
                <div
                  ref={ontCarouselRef}
                  onScroll={handleOntScroll}
                  className="ont-carousel"
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {slides}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
                  <button
                    onClick={() => irASlide(Math.max(0, ontActiveIndex - 1))}
                    disabled={ontActiveIndex === 0}
                    aria-label="Anterior"
                    style={{
                      border: 'none', background: 'none', padding: 2, fontSize: 12, lineHeight: 1,
                      color: ontActiveIndex === 0 ? 'var(--sn-borde-fuerte)' : 'var(--sn-marca)',
                      cursor: ontActiveIndex === 0 ? 'default' : 'pointer',
                    }}
                  >
                    ◀
                  </button>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {slides.map((_, i: number) => (
                      <span
                        key={i}
                        onClick={() => irASlide(i)}
                        style={{
                          width: 6, height: 6, borderRadius: '50%', cursor: 'pointer',
                          background: i === ontActiveIndex ? 'var(--sn-marca)' : 'var(--sn-borde-fuerte)',
                        }}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => irASlide(Math.min(slides.length - 1, ontActiveIndex + 1))}
                    disabled={ontActiveIndex === slides.length - 1}
                    aria-label="Siguiente"
                    style={{
                      border: 'none', background: 'none', padding: 2, fontSize: 12, lineHeight: 1,
                      color: ontActiveIndex === slides.length - 1 ? 'var(--sn-borde-fuerte)' : 'var(--sn-marca)',
                      cursor: ontActiveIndex === slides.length - 1 ? 'default' : 'pointer',
                    }}
                  >
                    ▶
                  </button>
                </div>
                {botonDispositivos}
              </div>
            ) : (
              <>
                {slides[0]}
                {botonDispositivos}
              </>
            );
          })()}

          {/* Recargar + versión — discretos al pie de la pantalla.
              Recargar hace un refresco completo (window.location.reload), que
              además reinicia el bot de soporte. */}
          <div className="text-center mt-3 mb-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => window.location.reload()}
              title="Recargar la aplicación"
              style={{
                background: 'transparent', border: '1px solid var(--sn-borde)', borderRadius: 999,
                padding: '4px 12px', cursor: 'pointer', color: 'var(--sn-texto-3)', fontSize: '0.72rem',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            >
              <span style={{ fontSize: '0.8rem' }}>🔄</span> Recargar
            </button>
            <span style={{ fontSize: '0.72rem', color: 'var(--sn-borde-fuerte)', cursor: 'pointer' }} onClick={() => setShowAyudaModal(true)}>
              v{APP_VERSION}
            </span>
          </div>
        </Container>
      </div>

      {/* Barra de navegación inferior (fija) + panel "Más" */}
      <BottomNav />

      {/* Modal de ayuda y soporte (disparado desde el texto de versión) */}
      <AyudaModal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} />

    </div>
  );
};

export default Menu;
